import React, { useEffect, useState } from 'react';
import { get, ref } from 'firebase/database';
import { database } from '../firebaseConfig';

// Shows on "Request Professional Staging" tasks: Email and Text buttons with an editable message.
// Nothing sends automatically; the agent opens the message, edits it if needed, and sends it from their phone or mail app.
const longDate = (value) => {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  return isNaN(d) ? String(value) : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const fill = (text, data) => String(text || '').replace(/\{\{(\w+)\}\}/g, (m, k) => (data[k] == null ? '' : String(data[k])));

const EMAIL_SUBJECT = 'New staging project at {{address}}';
const EMAIL_BODY = `Hi {{stager_first_name}},

This is {{agent_full_name}}. I have a new staging project for you at {{address}}, with an anticipated photography date of {{photography_date}}.

I'd like to discuss this project with you. Could you please give me a call at your earliest convenience?

{{agent_full_name}}
{{agent_phone}}
RESIDENCE | eXp Realty`;
const TEXT_BODY = `Hi {{stager_first_name}}, this is {{agent_first_name}}. I have a new staging project for you at {{address}}, with an anticipated photography date of {{photography_date}}. I'd like to discuss it with you. Could you give me a call at your earliest convenience? {{agent_phone}}`;

export default function StagingQuickContact({ task, deal, ownerUid }) {
  const taskName = task.name || task.title || '';
  const [stager, setStager] = useState(null);
  const [agent, setAgent] = useState(null);
  const [mode, setMode] = useState(null); // 'email' | 'text' | null
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const isStagingTask = /request professional staging/i.test(taskName);

  useEffect(() => {
    if (!isStagingTask) return;
    get(ref(database, 'partners')).then((snap) => {
      const list = snap.exists() ? Object.values(snap.val()) : [];
      const name = deal?.stager || '';
      const match = list.find((p) => p && p.partnerType === 'Stager'
        && `${p.firstName || ''} ${p.lastName || ''}`.trim() === name);
      setStager(match || null);
    }).catch((err) => console.error('Could not load stagers', err));
    if (ownerUid) {
      Promise.all([get(ref(database, `users/${ownerUid}`)), get(ref(database, `users/${ownerUid}/profile`))])
        .then(([userSnap, profileSnap]) => {
          const user = userSnap.exists() ? userSnap.val() : {};
          const profile = profileSnap.exists() ? profileSnap.val() : {};
          setAgent({
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            phone: profile.phone || '',
            email: user.email || '',
          });
        })
        .catch((err) => console.error('Could not load agent', err));
    }
  }, [isStagingTask, deal?.stager, ownerUid]);

  if (!isStagingTask) return null;

  const data = {
    address: deal?.propertyAddress || '',
    photography_date: longDate(deal?.photographyDate),
    stager_first_name: stager?.firstName || (deal?.stager || '').split(' ')[0] || '',
    agent_first_name: agent?.firstName || '',
    agent_full_name: [agent?.firstName, agent?.lastName].filter(Boolean).join(' '),
    agent_phone: agent?.phone || '',
  };
  const stagerEmail = stager?.email || '';
  const stagerDigits = (stager?.phone || '').replace(/[^0-9+]/g, '');

  const open = (kind) => {
    setMode(mode === kind ? null : kind);
    if (kind === 'email') {
      setSubject(fill(EMAIL_SUBJECT, data));
      setMessage(fill(EMAIL_BODY, data));
    } else {
      setMessage(fill(TEXT_BODY, data));
    }
  };

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
  const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', opacity: 0.5, cursor: 'not-allowed' };

  if (!deal?.stager) {
    return <div style={{ margin: '6px 0', fontSize: 13, color: '#5B6270' }}>No stager selected on this deal yet.</div>;
  }

  return (
    <div style={{ margin: '6px 0' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#1A1A1A' }}>Stager: <strong>{deal.stager}</strong>{stager?.phone ? ` · ${stager.phone}` : ''}</span>
        <button type="button" onClick={() => open('email')} disabled={!stagerEmail} style={stagerEmail ? btn : btnOff}>Email</button>
        <button type="button" onClick={() => open('text')} disabled={!stagerDigits} style={stagerDigits ? btn : btnOff}>Text</button>
        {!stager && <span style={{ fontSize: 12, color: '#5B6270' }}>This stager is not in Partners yet.</span>}
      </div>
      {mode === 'email' && stagerEmail && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, marginBottom: 8 }} />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={10} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <a href={`mailto:${stagerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`} style={btn}>Send email</a>
            <button type="button" onClick={() => setMode(null)} style={{ ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' }}>Cancel</button>
          </div>
        </div>
      )}
      {mode === 'text' && stagerDigits && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <a href={`sms:${stagerDigits}?body=${encodeURIComponent(message)}`} style={btn}>Send text</a>
            <button type="button" onClick={() => setMode(null)} style={{ ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
